import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, API, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { printReportCard } from '../lib/printReport';
import { Exam, Plus, Trash, Printer } from '@phosphor-icons/react';

const MANAGERS = ['global_admin', 'school_admin', 'principal', 'registrar'];
const MARKERS = [...MANAGERS, 'teacher'];
const MODERATORS = ['global_admin', 'school_admin', 'principal'];
const RS_TONE = { pending: 'bg-slate-100 text-slate-500', entry: 'bg-amber-100 text-amber-800', moderation: 'bg-orange-100 text-orange-700', published: 'bg-green-100 text-green-800' };
const emptySub = { subject: '', date: '', start_time: '09:00', duration_min: 120, max_marks: 100 };

const StudentExams = ({ exams }) => {
  const [resultsFor, setResultsFor] = useState(null);
  const { data: results } = useQuery({
    queryKey: ['exam-results', resultsFor],
    queryFn: () => api.get(`/exams/${resultsFor}/results`).then((r) => r.data),
    enabled: !!resultsFor,
  });
  const { data: reports } = useQuery({ queryKey: ['my-report-cards'], queryFn: () => api.get('/report-cards').then((r) => r.data) });
  const { data: brand } = useQuery({ queryKey: ['branding'], queryFn: () => api.get('/branding').then((r) => r.data) });
  const upcoming = (exams || []).filter((e) => e.status !== 'draft' && e.results_status !== 'published');
  const published = (exams || []).filter((e) => e.results_status === 'published');
  return (
    <div data-testid="exams-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-6 flex items-center gap-2"><Exam size={30} className="text-orange-600" weight="duotone" /> Exams</h1>
      <Tabs defaultValue="upcoming">
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="exams-tab-upcoming">Upcoming exams & hall tickets</TabsTrigger>
          <TabsTrigger value="results" data-testid="exams-tab-results">Results & report cards</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="mt-4 space-y-4">
          {upcoming.map((e) => (
            <div key={e.id} className="bg-white border border-slate-200 rounded-lg p-5 stagger-in" data-testid={`upcoming-exam-${e.id}`}>
              <p className="font-display font-bold text-lg">{e.name} <span className="text-xs font-normal text-slate-400">{e.term} · Grade {e.grade}</span></p>
              <p className="text-xs text-slate-500 mt-1">{e.subjects.map((s) => `${s.subject} - ${s.date} ${s.start_time || ''}`).join(' · ')}</p>
              <a data-testid={`exam-hallticket-${e.id}`} href={`${API}/exams/${e.id}/hall-tickets.pdf`}
                className="inline-block mt-3 text-xs bg-orange-600 text-white rounded px-2.5 py-1.5 hover:bg-orange-500">Download hall ticket PDF</a>
            </div>
          ))}
          {upcoming.length === 0 && <p className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-400" data-testid="upcoming-empty">No upcoming exams scheduled.</p>}
        </TabsContent>
        <TabsContent value="results" className="mt-4 space-y-4">
          {published.map((e) => (
            <div key={e.id} className="bg-white border border-slate-200 rounded-lg p-5 stagger-in" data-testid={`result-exam-${e.id}`}>
              <p className="font-display font-bold">{e.name} <span className="text-xs font-normal text-slate-400">{e.term} · Grade {e.grade}</span></p>
              <div className="flex gap-2 mt-2">
                <button data-testid={`exam-view-results-${e.id}`} onClick={() => setResultsFor(e.id)} className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-slate-900">View marks</button>
                <a data-testid={`exam-reportcards-${e.id}`} href={`${API}/exams/${e.id}/report-cards.pdf`} className="text-xs bg-orange-600 text-white rounded px-2.5 py-1.5 hover:bg-orange-500">Report card PDF</a>
              </div>
            </div>
          ))}
          {(reports || []).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="term-report-cards">
              <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Term report cards</p>
              {(reports || []).map((r) => (
                <div key={r.id} className="flex items-center gap-3 py-2 border-t border-slate-50 text-sm" data-testid={`term-rc-${r.id}`}>
                  <span className="flex-1">{r.student_name} - {r.term}</span>
                  <span className="font-display font-extrabold">{r.overall_pct}%</span>
                  <button data-testid={`term-rc-print-${r.id}`} onClick={() => printReportCard(r, brand?.school_name || undefined, brand?.has_logo ? `${API}/branding/file/logo` : null)}
                    className="text-xs border border-slate-200 rounded px-2 py-1 hover:border-slate-900 flex items-center gap-1"><Printer size={12} />Print</button>
                </div>
              ))}
            </div>
          )}
          {published.length === 0 && (reports || []).length === 0 && <p className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-400" data-testid="results-empty">No published results yet.</p>}
        </TabsContent>
      </Tabs>
      <Dialog open={!!resultsFor} onOpenChange={() => setResultsFor(null)}>
        <DialogContent data-testid="exam-results-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Results - {results?.exam?.name}</DialogTitle></DialogHeader>
          {results && (
            <div className="max-h-72 overflow-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="exam-results-list">
              {(results.marks || []).map((m) => (
                <div key={`${m.student_id}-${m.subject}`} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                  <span className="flex-1 truncate">{m.student_name}</span>
                  <span className="text-slate-400 text-xs">{m.subject}</span>
                  <span className="font-bold tabular-nums">{m.marks}/{m.max_marks}</span>
                </div>
              ))}
              {(results.marks || []).length === 0 && <p className="p-4 text-sm text-slate-400">No marks recorded.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function Exams() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isManager = MANAGERS.includes(user?.role);
  const isMarker = MARKERS.includes(user?.role);
  const { data: exams } = useQuery({ queryKey: ['exams'], queryFn: () => api.get('/exams').then((r) => r.data) });
  const { data: meta } = useQuery({ queryKey: ['meta-grades'], queryFn: () => api.get('/meta/grades').then((r) => r.data) });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: () => api.get('/tenants').then((r) => r.data), enabled: isManager });
  const school = (tenants || []).find((t) => t.type === 'school');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', term: 'Term 1', grade: '5', subjects: [{ ...emptySub }] });
  const [marksFor, setMarksFor] = useState(null);
  const [marksSubject, setMarksSubject] = useState('');
  const [marks, setMarks] = useState({});
  const [resultsFor, setResultsFor] = useState(null);
  const [remarksFor, setRemarksFor] = useState(null);
  const [remarkVals, setRemarkVals] = useState({});
  const { data: students } = useQuery({
    queryKey: ['exam-students', (marksFor || remarksFor)?.grade],
    queryFn: () => api.get('/students').then((r) => r.data.filter((s) => String(s.grade) === String((marksFor || remarksFor).grade) && s.status === 'active')),
    enabled: !!(marksFor || remarksFor),
  });
  const { data: savedRemarks } = useQuery({
    queryKey: ['exam-remarks', remarksFor?.id],
    queryFn: () => api.get(`/exams/${remarksFor.id}/remarks`).then((r) => r.data),
    enabled: !!remarksFor,
  });
  const { data: results } = useQuery({
    queryKey: ['exam-results', resultsFor],
    queryFn: () => api.get(`/exams/${resultsFor}/results`).then((r) => r.data),
    enabled: !!resultsFor,
  });

  const inval = () => qc.invalidateQueries({ queryKey: ['exams'] });
  const createMut = useMutation({
    mutationFn: () => api.post('/exams', { ...form, tenant_id: user?.tenant_id || school?.id, subjects: form.subjects.filter((s) => s.subject && s.date).map(({ _k, ...s }) => s) }),
    onSuccess: () => { toast.success('Exam created (draft)'); setCreateOpen(false); setForm({ name: '', term: 'Term 1', grade: '5', subjects: [{ ...emptySub }] }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const act = useMutation({
    mutationFn: ({ path, body }) => api.post(path, body || {}),
    onSuccess: (r, v) => { toast.success(v.msg || 'Done'); inval(); if (v.closeMarks) { setMarksFor(null); setMarks({}); } },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/exams/${id}`),
    onSuccess: () => { toast.success('Draft exam deleted'); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const saveMarks = () => {
    const entries = Object.entries(marks).map(([student_id, m]) => ({ student_id, marks: parseFloat(m) })).filter((e) => !isNaN(e.marks));
    if (!entries.length) return toast.error('Enter at least one mark');
    act.mutate({ path: `/exams/${marksFor.id}/marks`, body: { subject: marksSubject, entries }, msg: `Saved ${entries.length} marks`, closeMarks: false });
  };

  if (['student', 'guardian'].includes(user?.role)) return <StudentExams exams={exams} />;

  return (
    <div data-testid="exams-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Scheduling · hall tickets · seating · moderated results</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2"><Exam size={30} className="text-orange-600" weight="duotone" /> Exams</h1>
        </div>
        {isManager && <Button data-testid="new-exam-button" onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800"><Plus size={16} className="mr-1" /> New exam</Button>}
      </div>

      <div className="space-y-4">
        {(exams || []).map((e) => (
          <div key={e.id} data-testid={`exam-card-${e.id}`} className="bg-white border border-slate-200 rounded-lg p-5 stagger-in">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-display font-bold text-lg">{e.name}</p>
              <span className="text-xs text-slate-400">{e.term} · Grade {e.grade} · {e.subjects.length} papers</span>
              <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${e.status === 'draft' ? 'bg-slate-100 text-slate-500' : e.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-700'}`} data-testid={`exam-status-${e.id}`}>{e.status}</span>
              <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${RS_TONE[e.results_status]}`} data-testid={`exam-results-status-${e.id}`}>results: {e.results_status}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{e.subjects.map((s) => `${s.subject} (${s.date})`).join(' · ')}</p>
            <div className="flex gap-2 mt-3 flex-wrap items-center">
              {isManager && e.status === 'draft' && (
                <>
                  <button data-testid={`exam-schedule-${e.id}`} onClick={() => act.mutate({ path: `/exams/${e.id}/schedule`, msg: 'Exam scheduled - students notified' })}
                    className="text-xs bg-orange-600 text-white rounded px-2.5 py-1.5 hover:bg-orange-500">Schedule & notify</button>
                  <button data-testid={`exam-delete-${e.id}`} onClick={() => { if (window.confirm('Delete this draft exam?')) delMut.mutate(e.id); }}
                    className="text-xs border border-rose-200 text-rose-600 rounded px-2.5 py-1.5 hover:bg-rose-50"><Trash size={12} className="inline mr-0.5" />Delete</button>
                </>
              )}
              {isManager && (
                <button data-testid={`exam-seating-${e.id}`} onClick={() => {
                  const spec = window.prompt('Rooms as name:capacity, comma-separated (e.g. Hall-A:30, Hall-B:20):', 'Hall-A:40');
                  if (!spec) return;
                  const rooms = spec.split(',').map((x) => { const [name, cap] = x.split(':'); return { name: (name || '').trim(), capacity: parseInt(cap, 10) || 0 }; });
                  act.mutate({ path: `/exams/${e.id}/seating/generate`, body: { rooms }, msg: 'Seating plan generated' });
                }} className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-slate-900">Generate seating</button>
              )}
              {(e.seating || []).length > 0 && isMarker && <a data-testid={`exam-seating-pdf-${e.id}`} href={`${API}/exams/${e.id}/seating.pdf`} className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-orange-500">Seating PDF</a>}
              {e.status !== 'draft' && <a data-testid={`exam-hallticket-${e.id}`} href={`${API}/exams/${e.id}/hall-tickets.pdf`} className="text-xs border border-orange-200 text-orange-700 rounded px-2.5 py-1.5 hover:bg-orange-50">Hall ticket{isMarker ? 's' : ''} PDF</a>}
              {isMarker && e.results_status !== 'published' && (
                <button data-testid={`exam-marks-${e.id}`} onClick={() => { setMarksFor(e); setMarksSubject(e.subjects[0]?.subject || ''); setMarks({}); }}
                  className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-slate-900">Enter marks</button>
              )}
              {isMarker && e.results_status === 'entry' && (
                <button data-testid={`exam-submit-results-${e.id}`} onClick={() => act.mutate({ path: `/exams/${e.id}/results/submit`, msg: 'Results sent for moderation' })}
                  className="text-xs bg-slate-900 text-white rounded px-2.5 py-1.5 hover:bg-slate-800">Submit for moderation</button>
              )}
              {MODERATORS.includes(user?.role) && e.results_status === 'moderation' && (
                <>
                  <button data-testid={`exam-publish-${e.id}`} onClick={() => act.mutate({ path: `/exams/${e.id}/results/publish`, body: { note: window.prompt('Moderation note (optional):') || null }, msg: 'Results published' })}
                    className="text-xs bg-green-700 text-white rounded px-2.5 py-1.5 hover:bg-green-600">Publish results</button>
                  <button data-testid={`exam-reject-${e.id}`} onClick={() => act.mutate({ path: `/exams/${e.id}/results/reject`, body: { note: window.prompt('Reason for sending back:') || null }, msg: 'Sent back for re-entry' })}
                    className="text-xs border border-rose-200 text-rose-600 rounded px-2.5 py-1.5 hover:bg-rose-50">Send back</button>
                </>
              )}
              {(e.results_status === 'published' || isMarker) && e.results_status !== 'pending' && (
                <button data-testid={`exam-view-results-${e.id}`} onClick={() => setResultsFor(e.id)} className="text-xs text-blue-600 underline">View results</button>
              )}
              {isMarker && e.results_status !== 'pending' && (
                <button data-testid={`exam-remarks-${e.id}`} onClick={() => { setRemarksFor(e); setRemarkVals({}); }}
                  className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-slate-900">Remarks</button>
              )}
              {e.results_status === 'published' && (
                <a data-testid={`exam-reportcards-${e.id}`} href={`${API}/exams/${e.id}/report-cards.pdf`}
                  className="text-xs bg-orange-600 text-white rounded px-2.5 py-1.5 hover:bg-orange-500">Report card{isMarker ? 's' : ''} PDF</a>
              )}
            </div>
          </div>
        ))}
        {(exams || []).length === 0 && <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400" data-testid="exams-empty">No exams yet{isManager ? ' - create the first one.' : '.'}</div>}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="exam-create-dialog" className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New exam</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="exam-name-input" placeholder="Exam name (e.g. Mid-term Examination)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="flex gap-2">
              <Input data-testid="exam-term-input" placeholder="Term" value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} />
              <select data-testid="exam-grade-select" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })}
                className="w-40 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                {(meta?.grades || []).map((g) => <option key={g} value={g}>{/^\d+$/.test(g) ? `Grade ${g}` : g}</option>)}
              </select>
            </div>
            <p className="font-mono-label text-[10px] uppercase text-slate-400">Subject papers</p>
            {form.subjects.map((s, i) => (
              <div key={s._k || i} className="flex gap-1.5 flex-wrap items-center border border-slate-100 rounded-lg p-2">
                <Input data-testid={`exam-subject-${i}`} className="w-32 h-8 text-xs" placeholder="Subject" value={s.subject} onChange={(e) => setForm({ ...form, subjects: form.subjects.map((x, j) => j === i ? { ...x, subject: e.target.value } : x) })} />
                <Input data-testid={`exam-date-${i}`} type="date" className="w-36 h-8 text-xs" value={s.date} onChange={(e) => setForm({ ...form, subjects: form.subjects.map((x, j) => j === i ? { ...x, date: e.target.value } : x) })} />
                <Input type="time" className="w-24 h-8 text-xs" value={s.start_time} onChange={(e) => setForm({ ...form, subjects: form.subjects.map((x, j) => j === i ? { ...x, start_time: e.target.value } : x) })} />
                <Input type="number" className="w-20 h-8 text-xs" title="Max marks" value={s.max_marks} onChange={(e) => setForm({ ...form, subjects: form.subjects.map((x, j) => j === i ? { ...x, max_marks: parseInt(e.target.value, 10) || 100 } : x) })} />
                {form.subjects.length > 1 && <button onClick={() => setForm({ ...form, subjects: form.subjects.filter((_, j) => j !== i) })} className="text-rose-500 text-xs font-bold px-1">×</button>}
              </div>
            ))}
            <button data-testid="exam-add-subject" onClick={() => setForm({ ...form, subjects: [...form.subjects, { ...emptySub, _k: `${Date.now()}-${form.subjects.length}` }] })} className="text-xs text-orange-700 underline">+ Add subject</button>
            <Button data-testid="exam-create-submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!form.name || !form.subjects.some((s) => s.subject && s.date) || createMut.isPending} onClick={() => createMut.mutate()}>Create draft exam</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!marksFor} onOpenChange={() => setMarksFor(null)}>
        <DialogContent data-testid="exam-marks-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Enter marks - {marksFor?.name}</DialogTitle></DialogHeader>
          {marksFor && (
            <div className="space-y-3">
              <select data-testid="marks-subject-select" value={marksSubject} onChange={(e) => { setMarksSubject(e.target.value); setMarks({}); }}
                className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                {marksFor.subjects.map((s) => <option key={s.subject} value={s.subject}>{`${s.subject} (max ${s.max_marks})`}</option>)}
              </select>
              <div className="max-h-64 overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {(students || []).map((s) => (
                  <div key={s.id} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="text-sm flex-1 truncate">{s.name} <span className="text-slate-400 text-xs">{s.admission_no}</span></span>
                    <Input data-testid={`marks-input-${s.id}`} type="number" className="w-20 h-7 text-xs" placeholder="-" value={marks[s.id] ?? ''} onChange={(e) => setMarks({ ...marks, [s.id]: e.target.value })} />
                  </div>
                ))}
              </div>
              <Button data-testid="marks-save-button" className="w-full bg-slate-900 hover:bg-slate-800" onClick={saveMarks}>Save marks</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!remarksFor} onOpenChange={() => setRemarksFor(null)}>
        <DialogContent data-testid="exam-remarks-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Teacher remarks - {remarksFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Remarks appear on each student's term report card PDF.</p>
            <div className="max-h-72 overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {(students || []).map((s) => {
                const saved = (savedRemarks || []).find((r) => r.student_id === s.id)?.remark || '';
                return (
                  <div key={s.id} className="px-3 py-2">
                    <p className="text-sm font-medium">{s.name} <span className="text-slate-400 text-xs">{s.admission_no}</span></p>
                    <Input data-testid={`remark-input-${s.id}`} className="h-8 text-xs mt-1" placeholder={saved || 'Add a remark...'}
                      value={remarkVals[s.id] ?? saved} onChange={(ev) => setRemarkVals({ ...remarkVals, [s.id]: ev.target.value })} />
                  </div>
                );
              })}
            </div>
            <Button data-testid="remarks-save-button" className="w-full bg-slate-900 hover:bg-slate-800" onClick={() => {
              const entries = Object.entries(remarkVals).map(([student_id, remark]) => ({ student_id, remark }));
              if (!entries.length) return toast.error('Type at least one remark');
              act.mutate({ path: `/exams/${remarksFor.id}/remarks`, body: { entries }, msg: `Saved ${entries.length} remark(s)` });
              setRemarksFor(null);
            }}>Save remarks</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resultsFor} onOpenChange={() => setResultsFor(null)}>
        <DialogContent data-testid="exam-results-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Results - {results?.exam?.name}</DialogTitle></DialogHeader>
          {results && (
            <div>
              {results.exam.moderation_note && <p className="text-xs text-slate-500 mb-2">Moderation note: {results.exam.moderation_note}</p>}
              <div className="max-h-72 overflow-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="exam-results-list">
                {(results.marks || []).map((m) => (
                  <div key={`${m.student_id}-${m.subject}`} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                    <span className="flex-1 truncate">{m.student_name}</span>
                    <span className="text-slate-400 text-xs">{m.subject}</span>
                    <span className="font-bold tabular-nums">{m.marks}/{m.max_marks}</span>
                  </div>
                ))}
                {(results.marks || []).length === 0 && <p className="p-4 text-sm text-slate-400">No marks recorded.</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
